import { useCallback, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { ENTER_KEY } from 'common/constants/general';
import CompanyList from 'components/MeasurementDates/components/company-list/CompanyList';
import FundList from 'components/MeasurementDates/components/fund-list/FundList';
import { useMDContext } from 'context/MDContext';
import { dbShortDate, gridShortDate, nextQuarterEndDate } from 'utilities/dateFormats';
import { CompaniesSelectorProps } from './CompaniesSelector.props';
import { FullWidthDatePicker, MarginTopGrid } from './CompaniesSelector.styles';
import { SuggestedDate } from './suggested-date';
import getSuggestedQuarterFiscalEndDate, {
  isValidCompanyFiscalYear,
} from '../utilities/getSuggestedQuarterFiscalEndDate';

const isValidPickerDate = (newDate: Date | null): newDate is Date =>
  Boolean(newDate && !Number.isNaN(newDate.getTime()));

const CompaniesSelector = ({ activeCompany }: CompaniesSelectorProps) => {
  const {
    measurementDate,
    setMeasurementDate,
    errors,
    items,
    isLoading,
    setSelectedFunds,
    selectedFunds,
    selectedCompanies,
    setSelectedCompanies,
    measurementDateOptionsByCompanyId,
  } = useMDContext();
  const [date, setDate] = useState<Date | null>(measurementDate ? new Date(measurementDate) : null);

  // Suggest the fiscal quarter end that follows the measurement date this company rolls from.
  const suggestedDate = useMemo(() => {
    const fiscalYearEnd = activeCompany?.fiscal_year_end;
    if (!isValidCompanyFiscalYear(fiscalYearEnd)) {
      return nextQuarterEndDate();
    }

    // MeasurementDateDialog puts the NONE option first, so the most recent date is next.
    const [, lastMeasurementDate] = measurementDateOptionsByCompanyId[activeCompany?.id as number] ?? [];
    return getSuggestedQuarterFiscalEndDate(
      fiscalYearEnd,
      lastMeasurementDate ? dbShortDate(lastMeasurementDate) : null
    );
  }, [activeCompany, measurementDateOptionsByCompanyId]);

  const handleChange = useCallback(
    (newDate: Date | null) => {
      setDate(newDate);
      if (newDate === null) {
        setMeasurementDate(undefined);
      }
    },
    [setMeasurementDate]
  );

  const handleAccept = useCallback(
    (newDate: Date | null) => {
      if (!isValidPickerDate(newDate)) {
        setMeasurementDate(undefined);
        return;
      }
      setMeasurementDate(gridShortDate(newDate));
    },
    [setMeasurementDate]
  );

  /*
  Seeds the picker with today when empty so the date picker
  has a sensible anchor while the user is choosing a date.
  */
  const handleFocus = useCallback(() => {
    if (!date) {
      setDate(new Date());
    }
  }, [date]);

  /*
  Applies typed text when the user leaves the field.
  Keyboard entry never goes through onAccept.
  */
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = event.target.value.trim();
      if (inputValue === '') {
        setMeasurementDate(undefined);
        return;
      }
      const newDate = new Date(inputValue);
      if (measurementDate !== event.target.value) {
        handleAccept(newDate);
      }
    },
    [handleAccept, measurementDate, setMeasurementDate]
  );

  /*
  Commits or clears on Enter for the same reason as blur:
  typed values need an explicit commit path outside the
  calendar popup.
  */
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== ENTER_KEY) {
        return;
      }
      const inputValue = e.currentTarget.value.trim();
      if (inputValue === '') {
        setMeasurementDate(undefined);
        return;
      }
      handleAccept(new Date(inputValue));
    },
    [handleAccept, setMeasurementDate]
  );

  const handleClickSuggested = useCallback((suggestedDate: string) => {
    const newDate = new Date(suggestedDate);
    setDate(newDate);
    handleAccept(newDate);
  }, [handleAccept]);

  return (
    <>
      <Grid size={{ xs: 6 }}>
        <FullWidthDatePicker
          value={date}
          format="MM/dd/yyyy"
          onChange={handleChange}
          onAccept={handleAccept}
          openTo={'day'}
          views={['year', 'month', 'day']}
          label="Measurement Date"
          slotProps={{
            textField: {
              variant: 'standard',
              margin: 'normal',
              id: 'new-measurement-date',
              name: 'new-measurement-date',
              onFocus: handleFocus,
              onBlur: handleBlur,
              onKeyDown,
              helperText: errors.date,
              error: !!errors.date,
              placeholder: 'SELECT DATE',
            },
          }}
        />
        <SuggestedDate suggestedDate={suggestedDate} handleClickSuggested={handleClickSuggested} />
      </Grid>
      <MarginTopGrid container spacing={4}>
        <Grid size={{ xs: 6, md: 6 }}>
          <FundList
            items={items}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            setSelectedFunds={setSelectedFunds}
            selectedFunds={selectedFunds}
            isDeletingMD={false}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <CompanyList
            items={items}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            isDeletingMD={false}
          />
        </Grid>
      </MarginTopGrid>
    </>
  );
};

export default CompaniesSelector;
